export interface CalendarYearFaqItem {
  question: string;
  answer: string;
}

export interface CalendarYearSeo {
  title: string;
  description: string;
  keywords: string;
  h1: string;
  monthSectionH2: string;
  openingParagraphs: [string, string, string];
  faqItems: CalendarYearFaqItem[];
}

/**
 * 2026 and earlier stay calendar-intent. From 2027 the year page is also the
 * season page, which reverses the 2026-09-14 no-season rule for later years only.
 */
const SEASON_COPY_FROM_YEAR = 2027;

/** Season-guide posts. 2026 keeps the blog guide. Later years point at the on-page section. */
const SEASON_GUIDE_BY_YEAR: Partial<Record<number, { href: string; label: string }>> = {
  2026: {
    href: '/blog/milky-way-season-2026-planning-guide',
    label: 'Milky Way season 2026 planning guide',
  },
};

const PDF_FAQ = {
  question: 'Can I download the calendar as a PDF?',
  answer:
    'Not this grid. This page has no PDF download. In the planner, the PDF button is Premium: it shows only with an active subscription, and the download is refused without one. A shared calendar link has its own Download PDF button, and that one works for anyone with the link, no account needed.',
};

export function getRelatedSeasonGuide(year: number) {
  if (year >= SEASON_COPY_FROM_YEAR) {
    return { href: '#season', label: `When is Milky Way season ${year}?` };
  }
  return SEASON_GUIDE_BY_YEAR[year] ?? null;
}

export function getPlannerPageSeo() {
  return {
    title: 'Milky Way Visibility Planner: Nightly 1–10 Ratings',
    description:
      'Search any location for a 1–10 Milky Way rating on every night, with galactic core rise and set times, compass directions, and moon phase. Free to use.',
    keywords:
      'milky way calendar, milky way visibility, dark sky locations, best time to see milky way, galactic core visibility, astrophotography calendar, dark sky parks, bortle scale, milky way photography locations, when to see milky way, milky way rise time',
  };
}

export function getCalendarYearSeo(year: number): CalendarYearSeo {
  const seasonLed = year >= SEASON_COPY_FROM_YEAR;
  return {
    title: seasonLed
      ? `Milky Way Calendar ${year}: Season Dates & Best Nights`
      : `Milky Way Calendar ${year}: Best Nights by Location`,
    description: seasonLed
      ? `Milky Way season ${year} at 30+ dark-sky sites: first and last good Saturdays, the best nights of the year, and a free planner for your own spot.`
      : `Free ${year} Milky Way calendar: Saturday ratings for 30+ dark-sky sites, scored on core position and moon. Then check your own spot in the free planner.`,
    keywords: `milky way calendar ${year}, best time to photograph milky way ${year}, milky way photography calendar, galactic core visibility ${year}, dark sky locations, astrophotography planner ${year}`,
    h1: `Milky Way Calendar ${year}`,
    monthSectionH2: `What the ${year} calendar months look like`,
    openingParagraphs: [
      `This page is a ${year} Milky Way calendar for planning shoots by location. Each cell rates a Saturday night on galactic core position and moon phase, so you can see when the core is worth the trip before you book anything.`,
      'It is built for photographers who need dark nights at a specific place, not a generic "best month" list. Browse the location grid below, or open the free planner, enter coordinates, and get nightly ratings plus core rise and set times.',
      'Ratings stay honest: green means conditions look strong on paper. Weather and light pollution still sit with you.',
    ],
    faqItems: [
      {
        question: `What is the ${year} Milky Way calendar?`,
        answer:
          'A Saturday-night visibility overview for 30+ dark-sky locations, rated from galactic core position and moon phase so you can plan trips around workable nights.',
      },
      {
        question: 'How are the ratings calculated?',
        answer:
          'Core must be above the horizon in astronomical darkness; moon illumination weighs heavily (bright moon can zero a night even if the core is up).',
      },
      {
        question: 'Is the calendar free?',
        answer:
          'The yearly location overview and the free location calendar on milkywayplanner.com are usable without an account for basic planning; Premium adds exports and trip tools.',
      },
      {
        question: 'How do I get nightly detail for my pin?',
        answer:
          'Open the free planner, set your coordinates (or a listed site), and read the monthly calendar with core timing and moon.',
      },
      PDF_FAQ,
    ],
  };
}
